/**
 * Question panel component.
 *
 * Displays questions with progress tracking. Supports both reading and listening modules.
 * Unified component that handles both native scrolling and Radix ScrollArea.
 *
 * Adopts the shared simulation primitives (`SimulationPanelHeader`,
 * `SimulationTaskProgress`) so Reading/Listening match the Writing module
 * chrome exactly. The pill + bar are rendered together via
 * `SimulationTaskProgress` directly below the panel header.
 *
 * @module QuestionPanel
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, ListChecks } from "lucide-react";
import { QuestionCard } from "./QuestionCard";
import { Button } from "@/components/ui/button";
import { SimulationQuestionNavigatorDialog } from "./SimulationQuestionNavigatorDialog";
import { SimulationPanelHeader } from "./shared/SimulationPanelHeader";
import { SimulationTaskProgress } from "./shared/SimulationTaskProgress";
import { useMcqKeyboardNavigation } from "@/hooks/useMcqKeyboardNavigation";
import type { Question } from "@/types/exam-simulation";
import type { SimulationNavItem } from "@/utils/simulationQuestionNavigation";
import { getMcqFocusDomId } from "@/utils/mcqKeyboardNavigation";

function scrollToTop(container: HTMLElement, scrollType: string) {
  if (scrollType === "radix") {
    const viewport = container.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTo({ top: 0, behavior: "smooth" });
  } else {
    container.scrollTo({ top: 0, behavior: "smooth" });
  }
}

export interface QuestionPanelNavigatorConfig {
  readonly items: SimulationNavItem[];
  readonly onJump: (itemId: string) => void;
  /** When false, dialog lists items but does not jump (unused for reading/listening when navigator omitted). */
  readonly jumpEnabled?: boolean;
}

interface QuestionPanelProps {
  readonly questions: Question[];
  readonly activeQuestionId: number | null;
  readonly selectedAnswers: Record<number, string>;
  readonly struckAnswers: Record<number, string[]>;
  readonly flaggedQuestions: number[];
  readonly isSubmitted?: boolean;
  readonly evaluationByQuestion?: Record<
    number,
    {
      readonly selectedIsCorrect?: boolean | null;
      readonly expectedAnswerId?: string | null;
    }
  >;
  readonly totalQuestions: number;
  readonly currentIndex: number;
  readonly scrollType?: "native" | "radix";
  readonly scrollTrigger?: number;
  readonly onQuestionSelect: (questionId: number) => void;
  readonly onAnswerSelect: (questionId: number, answerId: string) => void;
  readonly onStrikeToggle: (questionId: number, answerId: string) => void;
  readonly onFlagToggle: (questionId: number) => void;
  /** Optional: overview dialog for unanswered questions / jump to item */
  readonly questionNavigator?: QuestionPanelNavigatorConfig | null;
}

export function QuestionPanel({
  questions,
  activeQuestionId,
  selectedAnswers,
  struckAnswers,
  flaggedQuestions,
  isSubmitted = false,
  evaluationByQuestion = {},
  totalQuestions,
  currentIndex,
  scrollType = "native",
  scrollTrigger = 0,
  onQuestionSelect,
  onAnswerSelect,
  onStrikeToggle,
  onFlagToggle,
  questionNavigator = null,
}: QuestionPanelProps) {
  const { t } = useTranslation();
  const [navigatorOpen, setNavigatorOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isMatchingOnly =
    questions.length > 0 && questions.every((q) => q.type === "matching");
  const keyboardNavEnabled =
    questions.length > 0 && !isMatchingOnly && !isSubmitted && !navigatorOpen;

  const keyboardNav = useMcqKeyboardNavigation({
    questions,
    struckAnswers,
    activeQuestionId,
    enabled: keyboardNavEnabled,
    onQuestionSelect,
    onAnswerSelect,
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (activeQuestionId !== null) {
      const timeoutId = setTimeout(() => {
        const el = container.querySelector(
          `[data-question-id="${activeQuestionId}"]`,
        );
        if (el instanceof HTMLElement) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        scrollToTop(container, scrollType);
      }, 150);
      return () => clearTimeout(timeoutId);
    }

    scrollToTop(container, scrollType);
  }, [activeQuestionId, currentIndex, questions, scrollType, scrollTrigger]);

  const answeredCount = Object.keys(selectedAnswers).length;
  const qAbbr = t("simulation.active.questionAbbr", { defaultValue: "Q" });
  const questionRange =
    questions.length > 0
      ? `${qAbbr}${questions[0]?.id}–${qAbbr}${questions.at(-1)?.id ?? ""}`
      : "";

  const hasNavigator = Boolean(
    questionNavigator && questionNavigator.items.length > 0,
  );

  const overviewButton = hasNavigator ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 px-2 sm:px-3 gap-1 text-[10px] sm:text-xs font-ui font-semibold"
      onClick={() => setNavigatorOpen(true)}
      aria-label={t("simulation.active.openQuestionList", {
        defaultValue: "Question overview",
      })}
    >
      <ListChecks className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden />
      <span className="hidden sm:inline">
        {t("simulation.active.openQuestionList", { defaultValue: "Overview" })}
      </span>
    </Button>
  ) : null;

  return (
    <div className="flex flex-col h-full bg-exam-paper border-l-0 lg:border-l border-border">
      <SimulationPanelHeader
        icon={<HelpCircle className="w-5 h-5" />}
        title={t("simulation.active.questions", { defaultValue: "Questions" })}
        titleSuffix={questionRange}
        className="border-b-0 pb-0 sm:pb-0"
      />

      <div className="px-4 sm:px-6 pb-3 sm:pb-4 border-b border-border bg-card/50 shrink-0">
        <SimulationTaskProgress
          current={answeredCount}
          total={totalQuestions}
          trailingAccessory={overviewButton}
        />
      </div>

      {hasNavigator && questionNavigator ? (
        <SimulationQuestionNavigatorDialog
          open={navigatorOpen}
          onOpenChange={setNavigatorOpen}
          items={questionNavigator.items}
          onJump={questionNavigator.onJump}
          jumpEnabled={questionNavigator.jumpEnabled !== false}
        />
      ) : null}

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 sm:space-y-4 sm:p-6"
      >
        {keyboardNavEnabled ? (
          <button
            type="button"
            className="sr-only font-ui"
            onFocus={keyboardNav.onEntryFocus}
          >
            {t("simulation.active.keyboardNavEntry", {
              defaultValue: "Start keyboard navigation for questions",
            })}
          </button>
        ) : null}

        {questions.map((question, index) => {
          const questionDomId = getMcqFocusDomId({
            kind: "question",
            questionId: question.id,
          });

          return (
            <div
              key={question.id}
              data-question-id={question.id}
              className="animate-in fade-in slide-in-from-bottom-2 duration-300"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <QuestionCard
                question={question}
                isActive={activeQuestionId === question.id}
                isFlagged={flaggedQuestions.includes(question.id)}
                selectedAnswer={selectedAnswers[question.id] || null}
                struckAnswers={struckAnswers[question.id] || []}
                isSubmitted={isSubmitted}
                evaluation={evaluationByQuestion[question.id] ?? null}
                onSelect={() => onQuestionSelect(question.id)}
                onAnswerSelect={(answerId) =>
                  onAnswerSelect(question.id, answerId)
                }
                onStrikeToggle={(answerId) =>
                  onStrikeToggle(question.id, answerId)
                }
                onFlagToggle={() => onFlagToggle(question.id)}
                questionFocusProps={
                  keyboardNavEnabled
                    ? keyboardNav.getFocusProps(questionDomId)
                    : null
                }
                getOptionFocusProps={
                  keyboardNavEnabled
                    ? (optionId) =>
                        keyboardNav.getFocusProps(
                          getMcqFocusDomId({
                            kind: "option",
                            questionId: question.id,
                            optionId,
                          }),
                        )
                    : undefined
                }
                isQuestionKeyboardFocused={
                  keyboardNavEnabled
                    ? keyboardNav.isTargetFocused(questionDomId)
                    : false
                }
                isOptionKeyboardFocused={
                  keyboardNavEnabled
                    ? (optionId) =>
                        keyboardNav.isTargetFocused(
                          getMcqFocusDomId({
                            kind: "option",
                            questionId: question.id,
                            optionId,
                          }),
                        )
                    : undefined
                }
              />
            </div>
          );
        })}
      </div>

      <div className="safe-area-bottom shrink-0 border-t border-border bg-card/30 px-3 pt-2 sm:px-6 sm:py-3">
        <div className="flex flex-col items-center gap-2 sm:gap-0">
          <div className="flex items-center justify-center gap-4 sm:gap-6 font-ui text-[10px] sm:text-xs text-muted-foreground overflow-x-auto no-scrollbar w-full">
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <div className="w-2.5 h-2.5 rounded border border-primary bg-primary/15" />
              <span>
                {t("simulation.active.legend.chosen", {
                  defaultValue: "Chosen",
                })}
              </span>
            </div>
            {!isMatchingOnly ? (
              <div className="flex items-center gap-1.5 whitespace-nowrap">
                <div className="w-2.5 h-2.5 rounded bg-muted/60" />
                <span className="line-through opacity-70">
                  {t("simulation.active.legend.eliminated", {
                    defaultValue: "Eliminated",
                  })}
                </span>
              </div>
            ) : null}
            <div className="flex items-center gap-1.5 whitespace-nowrap">
              <div className="w-2.5 h-2.5 rounded bg-exam-flag/20 border border-exam-flag" />
              <span>
                {t("simulation.active.legend.flagged", {
                  defaultValue: "Flagged for review",
                })}
              </span>
            </div>
          </div>
          {keyboardNavEnabled ? (
            <p className="font-ui text-[10px] sm:text-xs text-muted-foreground text-center">
              {t("simulation.active.legend.keyboardNav", {
                defaultValue: "Tab: next question or option · Space: select",
              })}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
